import React from 'react';
import HouseholdCard from './HouseholdCard';
import { HomeIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useHouseholdList } from '../hooks/useHouseholdList';
import { Alert, Card, EmptyState, Skeleton, cx, focusRing } from './ui';

const GRID = 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3';

const HouseholdList: React.FC<{ onAddHousehold: () => void }> = ({ onAddHousehold }) => {
  const { 
    households, 
    loading, 
    error, 
    isEmpty, 
  } = useHouseholdList();

  if (loading) {
    return (
      <div className={GRID} role="status" aria-label="Loading households…">
        {[0, 1, 2].map(i => (
          <Card key={i} className="space-y-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
            <Skeleton className="h-14 w-full rounded-lg" />
            <Skeleton className="h-8 w-full" />
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return <Alert tone="error" title="Couldn't load your households">{error}</Alert>;
  }

  if (isEmpty) {
    return (
      <Card padding="none">
        <EmptyState
          icon={HomeIcon}
          title="No households yet"
          description="A household is the home, cabin or office whose chores you share. Add your first one to get started."
        />
      </Card>
    );
  }

  return (
    <div className={GRID}>
      {households.map((household) => (
        <HouseholdCard key={household.household.id} household={household} />
      ))}
      <button
        type="button"
        onClick={onAddHousehold}
        className={cx(
          'flex min-h-40 flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 p-6 text-center text-gray-500 transition-colors hover:border-indigo-300 hover:bg-indigo-50/50 hover:text-indigo-700',
          'dark:border-gray-700 dark:text-gray-400 dark:hover:border-indigo-400/50 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-300',
          focusRing,
        )}
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
          <PlusIcon className="h-5 w-5" aria-hidden="true" />
        </span>
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">New household</span>
        <span className="text-sm">A home, a cabin, a shared office</span>
      </button>
    </div>
  );
};

export default HouseholdList;